import './filter-modal.scss';

import { Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import { statusOptions } from '@/components/App/AutoCred/data/statusOptions.data';
import type useFilterParamValues from '@/components/App/AutoCred/hooks/useFilterParamValues';
import type useFilters from '@/components/App/AutoCred/hooks/useFilters';
import { DictTypes } from '@/constants';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import {
  Button,
  DateRangePickerField,
  Multiselect,
  Select
} from '../../../../Shared/ui';
import { type IMultiselectOption } from '../../../../Shared/ui/multiselect/multiselect';
import { onlineOptions } from '../../data/onlineRequest.data';

type IProps = {
  selectedUser: string | null;
  isOpen: boolean;
  onClose: () => void;
  autoTypes: {
    label: string;
    value: string;
  }[];
  activeAutoType:
    | {
        label: string;
        value: string;
      }
    | undefined;
  userSelectOptions: IMultiselectOption[];
  dealerOptions: IMultiselectOption[];
  bankOptions: IMultiselectOption[];
} & { filterParamValues: ReturnType<typeof useFilterParamValues> } & Pick<
    ReturnType<typeof useFilters>,
    'handleApplyFilter'
  >;

interface FormValues {
  autoType: string | null;
  banks: string[];
  dealers: string[];
  models: string[];
  onlineRequest: string;
  period: string[];
  statuses: string[];
  user: string | null;
}

const FilterModal: React.FC<IProps> = ({
  selectedUser,
  isOpen,
  onClose,
  autoTypes,
  activeAutoType,
  userSelectOptions,
  dealerOptions,
  bankOptions,
  filterParamValues,
  handleApplyFilter
}) => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { dealer: userDealer, jwtDecodedInfo } = useAppSelector(
    (state) => state.userInfo
  );

  const { control, reset, getValues, setValue, handleSubmit } =
    useForm<FormValues>({
      mode: 'all',
      defaultValues: {
        user: null,
        dealers: [],
        period: [],
        models: [],
        statuses: [],
        autoType: null,
        onlineRequest: ''
      },
      delayError: 200
    });
  const [modelOptions, setModelOptions] = useState<IMultiselectOption[]>([]);

  const isHasDealersSelect = jwtDecodedInfo?.actions.includes(2);

  const handleResetFilters = () => {
    const userDealerItem =
      dicts?.[DictTypes.Dealers]?.getBy.code[safeKey(userDealer?.code)];
    const dealers = userDealerItem ? [userDealerItem.code.toString()] : [];
    reset({
      autoType: autoTypes[0].value,
      banks: [],
      dealers,
      models: [],
      onlineRequest: 'all',
      period: [],
      statuses: [],
      user: '-1'
    });
    setModelOptions(
      filterParamValues.getModelOptionsBySelectedDealers(dealers)
    );
  };

  const handleClose = () => {
    onClose();
  };

  const onOk: SubmitHandler<FormValues> = (data) => {
    handleApplyFilter({
      ...data,
      user: data.user || '-1',
      autoType: data.autoType || autoTypes[0].value,
      onlineRequest: data.onlineRequest || 'all'
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen && activeAutoType) {
      const dealers = filterParamValues.activeParamDealers.map(
        (item) => item.value
      );
      setModelOptions(
        filterParamValues.getModelOptionsBySelectedDealers(dealers)
      );
      reset({
        user: selectedUser,
        dealers: dealers,
        banks: filterParamValues.activeParamBanks.map((item) => item.value),
        period: filterParamValues.activeParamPeriod,
        models: filterParamValues.activeParamModels.map((item) => item.value),
        statuses: filterParamValues.activeParamStatuses.map(
          (item) => item.value
        ),
        autoType: activeAutoType.value,
        onlineRequest: filterParamValues.activeOnlineRequest
      });
    }
  }, [isOpen]);

  return (
    <Modal
      centered
      className="filter-modal"
      title="Автокредитование"
      open={isOpen}
      onCancel={handleClose}
      mask={{ closable: true }}
      footer={null}
    >
      <p className="filter-modal__subtitle">Выберите нужные фильтры</p>

      <form className="filter-modal__form" onSubmit={handleSubmit(onOk)}>
        <Controller
          control={control}
          name="autoType"
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Тип авто"
              selectedValue={autoTypes.find(
                (item) => item.value === field.value
              )}
              onChange={({ value }) => field.onChange(value)}
              options={autoTypes}
              error={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="user"
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Заявки"
              selectedValue={userSelectOptions.find(
                (item) => item.value === field.value
              )}
              onChange={({ value }) => field.onChange(value)}
              options={userSelectOptions}
              error={error?.message}
            />
          )}
        />

        {isHasDealersSelect && (
          <Controller
            control={control}
            name="dealers"
            rules={{ required: 'Пожалуйста, выберите дилера' }}
            render={({ field, fieldState: { error } }) => (
              <Multiselect
                {...field}
                isRequired
                label="Дилерские центры"
                selectedValues={dealerOptions.filter((item) =>
                  field.value.includes(item.value)
                )}
                onChange={(selectedValues) => {
                  const dealers = selectedValues.map((item) => item.value);
                  const newModelOptions =
                    filterParamValues.getModelOptionsBySelectedDealers(dealers);
                  setModelOptions(newModelOptions);
                  setValue(
                    'models',
                    getValues('models').filter((model) =>
                      newModelOptions.find((item) => item.value === model)
                    )
                  );
                  return field.onChange(dealers);
                }}
                options={dealerOptions}
                error={error?.message}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="period"
          render={({ field, fieldState: { error } }) => (
            <DateRangePickerField
              {...field}
              label="Период"
              onChange={(value) => field.onChange(value)}
              error={error?.message}
              isClearable
            />
          )}
        />

        <Controller
          control={control}
          name="models"
          render={({ field, fieldState: { error } }) => (
            <Multiselect
              {...field}
              label="Модель"
              selectedValues={modelOptions.filter((item) =>
                field.value.includes(item.value)
              )}
              onChange={(selectedValues) =>
                field.onChange(selectedValues.map((item) => item.value))
              }
              options={modelOptions}
              error={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="statuses"
          render={({ field, fieldState: { error } }) => (
            <Multiselect
              {...field}
              label="Статус"
              options={statusOptions}
              selectedValues={statusOptions.filter((item) =>
                field.value.includes(item.value)
              )}
              onChange={(selectedValues) =>
                field.onChange(selectedValues.map((item) => item.value))
              }
              error={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="banks"
          render={({ field, fieldState: { error } }) => (
            <Multiselect
              {...field}
              label="Банк"
              options={bankOptions}
              selectedValues={bankOptions.filter((item) =>
                field.value.includes(item.value)
              )}
              onChange={(selectedValues) =>
                field.onChange(selectedValues.map((item) => item.value))
              }
              error={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="onlineRequest"
          render={({ field }) => (
            <Select
              {...field}
              label="Онлайн / Оффлайн"
              options={onlineOptions}
              selectedValue={onlineOptions.find(
                (item) => item.value === field.value
              )}
              onChange={({ value }) => field.onChange(value)}
            />
          )}
        />

        <div className="filter-modal__actions">
          <Button onClick={handleResetFilters} type="button">
            Сбросить
          </Button>
          <Button variant="filled-red" type="submit">
            Применить
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FilterModal;
